import {z} from 'zod';
import {EventTag} from './eventTag';
import {PrerequisiteStatus, Registration} from './registration';
import {Discipline, EventType} from '@wayra/helpers';

export {Discipline, EventType};

export const TrackPoint = z.object({
  R: z.number().nullable().optional(),
  S: z.number().nullable().optional(),
  d: z.number().nullable().optional(),
  ele: z.number().nullable().optional(),
  lat: z.number().nullable().optional(),
  lng: z.number().nullable().optional(),
});

export const CategoryPrice = z.object({
  id: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  name: z.string().nullable(),
  amount: z.number(), // in cents
  currency: z.string(),
  endsAt: z.date().nullable(),
  categoryId: z.string(),
});

export const Category = z.object({
  id: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  name: z.string(),
  description: z.string().nullable(),
  categoryGroup: z.string().nullable(),
  categoryGroupSortOrder: z.number().nullable(),
  sortOrder: z.number(),
  maxParticipants: z.number().nullable(),
  participantCount: z.number().nullable(),
  spotsRemaining: z.number().nullable(),
  isOpen: z.boolean(),
  isSoldOut: z.boolean(),
  startTime: z.date().nullable(),
  eventId: z.string(),
  routeId: z.string().nullable(),
  prices: z.array(CategoryPrice).optional(),
});

export const Waypoint = z.object({
  name: z.string().nullable(),
  description: z.string().nullable().optional(),
  symbol: z.string().nullable().optional(),
  lat: z.number(),
  lng: z.number(),
  ele: z.optional(z.number()),
});

export const Route = z.object({
  id: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  name: z.string(),
  description: z.string().nullable(),
  distance_meters: z.number().nullable(),
  elevation_gain_meters: z.number().nullable(),
  track_points: z.array(TrackPoint).nullable().optional(),
  waypoints: z.array(Waypoint).nullable().optional(),
  routeFileUrl: z.string().nullable(),
  eventId: z.string(),
  categories: z.array(Category).optional(),
});

export const Organizer = z.object({
  id: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  name: z.string(),
  description: z.string().nullable(),
  website: z.string().nullable(),
  contactEmail: z.string().nullable(),
  logoUrl: z.string().nullable(),
  isVerified: z.boolean(),
  canAcceptPayments: z.boolean(),
  commissionBps: z.number(),
  stripeConnectId: z.string().nullable(),
});

export const OrganizerDetail = Organizer.extend({
  totalEvents: z.number(),
  totalParticipants: z.number(),
  totalAdmins: z.number(),
});

export const EventPrerequisite = z.object({
  id: z.string(),
  eventId: z.string(),
  prerequisiteEventId: z.string(),
  prerequisiteEvent: z.object({
    id: z.string(),
    name: z.string(),
    startDate: z.date().nullable(),
  }),
});
export type EventPrerequisite = z.infer<typeof EventPrerequisite>;

export const Event = z.object({
  id: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  editedAt: z.date().nullable(),
  heroUrl: z.string().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  editorialContent: z.string().nullable().optional(),
  editorialAuthor: z.object({
    firstName: z.string().nullable(),
    avatarUrl: z.string().nullable(),
  }).nullable().optional(),
  eventType: EventType,
  discipline: Discipline,
  governingBody: z.string().nullable(),
  sourceUrl: z.string().nullable(),
  lat: z.number(),
  lng: z.number(),
  location: z.string().nullable(),
  externalId: z.string().nullable(),
  startDate: z.date().nullable(),
  endDate: z.date().nullable(),
  timezone: z.string().nullable(),
  archived: z.boolean(),
  skipRiderFields: z.boolean(),
  requiresClubMembership: z.boolean().default(false),
  organizerId: z.string().nullable(),
  organizer: Organizer.nullable().optional(),
  routes: z.array(Route).optional(),
  categories: z.array(Category).optional(),
  prerequisites: z.array(EventPrerequisite).optional(),
  tags: z.array(EventTag).optional(),
});

/** Minimal route shape used in map event display */
export const MapEventRoute = z.object({
  distance_meters: z.number().nullable(),
  elevation_gain_meters: z.number().nullable(),
});

/** Minimal event shape used for all map display — dots, popups, and detail cards */
export const MapEvent = Event.pick({
  id: true,
  lat: true,
  lng: true,
  name: true,
  eventType: true,
  discipline: true,
  startDate: true,
  endDate: true,
  location: true,
  heroUrl: true,
  editorialContent: true,
}).extend({
  routes: z.array(MapEventRoute),
  organizer: Organizer.pick({name: true}).nullable().optional(),
});

/** Event shape for the homepage "Coming up" cards, with editorial HTML reduced to a plain-text excerpt */
export const UpcomingEvent = MapEvent.omit({
  lat: true,
  lng: true,
  editorialContent: true,
}).extend({
  editorialAuthor: Event.shape.editorialAuthor,
  editorialExcerpt: z.string().nullable(),
});

export type UpcomingEvent = z.infer<typeof UpcomingEvent>;

export const ParticipantPreview = z.object({
  id: z.string(),
  registeredAt: z.coerce.date(),
  profile: z.object({
    id: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    displayName: z.string().nullable(),
    avatarUrl: z.string().nullable(),
  }),
});

export const OrganizerAdminStatus = z.object({
  isAdmin: z.boolean(),
  isClaimable: z.boolean(),
});

export const AttendanceStatus = z.object({
  isAttending: z.boolean(),
});

export const RegistrationStatus = z.object({
  isRegistered: z.boolean(),
  registration: Registration.nullable(),
});

export const EventParticipants = z.object({
  participants: z.array(ParticipantPreview),
  totalCount: z.number(),
});

export const MembershipStatus = z.object({
  isMember: z.boolean(),
});
export type MembershipStatus = z.infer<typeof MembershipStatus>;

export const EventPageData = z.object({
  event: Event,
  shareDescription: z.string().nullable(),
  registrationStatus: RegistrationStatus,
  attendanceStatus: AttendanceStatus,
  savedStatus: z.object({
    isSaved: z.boolean(),
  }),
  prerequisiteStatus: PrerequisiteStatus,
  participants: EventParticipants,
  organizerAdminStatus: OrganizerAdminStatus,
  membershipStatus: MembershipStatus,
});

// Type exports
export type TrackPoint = z.infer<typeof TrackPoint>;
export type CategoryPrice = z.infer<typeof CategoryPrice>;
export type Category = z.infer<typeof Category>;
export type Route = z.infer<typeof Route>;
export type Organizer = z.infer<typeof Organizer>;
export type OrganizerDetail = z.infer<typeof OrganizerDetail>;
export type Event = z.infer<typeof Event>;
export type MapEventRoute = z.infer<typeof MapEventRoute>;
export type MapEvent = z.infer<typeof MapEvent>;
export type ParticipantPreview = z.infer<typeof ParticipantPreview>;
export type OrganizerAdminStatus = z.infer<typeof OrganizerAdminStatus>;
export type EventPageData = z.infer<typeof EventPageData>;
export type AttendanceStatus = z.infer<typeof AttendanceStatus>;
export type EventRegistrationStatus = z.infer<typeof RegistrationStatus>;
export type EventParticipants = z.infer<typeof EventParticipants>;
export {EventTag} from './eventTag';

// Event with all relations included
export type EventWithRelations = Event & {
  organizer: Organizer | null;
  routes: (Route & {
    categories: (Category & {
      prices: CategoryPrice[];
    })[];
  })[];
  categories: (Category & {
    prices: CategoryPrice[];
  })[];
};
